/*
  Product section JavaScript - handles variant selection, quantity controls, and area calculator
  
  This file contains all the JavaScript functionality for the product page.
  It's included in the product.liquid section.
*/

class ProductSection {
  static initializedSections = new WeakSet();

  constructor(element) {
    if (ProductSection.initializedSections.has(element)) {
      console.warn(
        "ProductSection already initialized for this element, skipping:",
        element
      );
      return;
    }
    ProductSection.initializedSections.add(element);

    this.section = element;
    this.productId = this.section.dataset.productId;
    this.sectionId = this.section.dataset.sectionId;

    // Form elements
    this.form = this.section.querySelector('form[data-type="add-to-cart-form"]') || this.section.querySelector('form[action*="/cart/add"]') || this.section.querySelector('form');
    this.variantSelect = this.section.querySelector("[data-variant-select]");
    this.quantityInput = this.section.querySelector("[data-quantity-input]");
    this.quantityMinus = this.section.querySelector("[data-quantity-minus]");
    this.quantityPlus = this.section.querySelector("[data-quantity-plus]");
    this.addToCartBtn = this.section.querySelector("[data-add-to-cart]");

    // Display elements
    this.priceElements = this.section.querySelectorAll("[data-price]");
    this.comparePriceElements = this.section.querySelectorAll(
      "[data-compare-price]"
    );
    this.availabilityElements = this.section.querySelectorAll(
      "[data-availability]"
    );
    this.skuElements = this.section.querySelectorAll("[data-sku]");

    // Live regions for accessibility
    this.priceLiveRegion = this.section.querySelector('[aria-live="polite"]');

    this.init();
  }

  init() {
    this.bindEvents();
    this.updateVariantInfo();
    this.setupAccessibility();
    this.updateQuantityButtonStates();
  }

  bindEvents() {
    // Variant selection
    if (this.variantSelect) {
      this.variantSelect.addEventListener("change", () =>
        this.handleVariantChange()
      );
    }

    // Quantity controls
    if (this.quantityMinus) {
      this.quantityMinus.addEventListener("click", () =>
        this.decreaseQuantity()
      );
    }
    if (this.quantityPlus) {
      this.quantityPlus.addEventListener("click", () =>
        this.increaseQuantity()
      );
    }
    if (this.quantityInput) {
      this.quantityInput.addEventListener("change", () =>
        this.validateQuantity()
      );
      this.quantityInput.addEventListener("input", () =>
        this.validateQuantity()
      );
    }

    // Add to cart
    if (this.form) {
      this.form.addEventListener("submit", (e) => this.handleAddToCart(e));
    } else {
      console.warn("Product form not found in section:", this.section);
    }

    // Keyboard navigation
    this.section.addEventListener("keydown", (e) => this.handleKeyboard(e));
  }

  setupAccessibility() {
    // Add live regions for dynamic content
    if (!this.priceLiveRegion) {
      const liveRegion = document.createElement("div");
      liveRegion.setAttribute("aria-live", "polite");
      liveRegion.setAttribute("aria-atomic", "true");
      liveRegion.className = "sr-only";
      liveRegion.id = "price-live-region";
      this.section.appendChild(liveRegion);
      this.priceLiveRegion = liveRegion;
    }

    // Setup details/summary accessibility
    const details = this.section.querySelectorAll("details");
    details.forEach((detail) => {
      const summary = detail.querySelector("summary");
      if (summary) {
        summary.addEventListener("click", () => {
          setTimeout(() => {
            const isOpen = detail.hasAttribute("open");
            summary.setAttribute("aria-expanded", isOpen.toString());
          }, 0);
        });
      }
    });
  }

  handleVariantChange() {
    const selectedOption =
      this.variantSelect.options[this.variantSelect.selectedIndex];
    const variantData = {
      id: selectedOption.value,
      price: selectedOption.dataset.price,
      comparePrice: selectedOption.dataset.comparePrice,
      available: selectedOption.dataset.available === "true",
      sku: selectedOption.dataset.sku,
    };

    this.updatePriceDisplay(variantData);
    this.updateAvailability(variantData);
    this.updateSku(variantData);
    this.updateAddToCartButton(variantData);
    this.updateUrl(variantData);
    this.updateImages(variantData);
    this.updatePickupAvailability(variantData);

    // Announce changes to screen readers
    this.announceVariantChange(variantData);
  }

  updatePriceDisplay(variantData) {
    const price = this.formatMoney(variantData.price);
    const comparePrice = variantData.comparePrice
      ? this.formatMoney(variantData.comparePrice)
      : null;

    this.priceElements.forEach((element) => {
      element.textContent = price;
      element.setAttribute("data-price", variantData.price);
    });

    this.comparePriceElements.forEach((element) => {
      if (comparePrice && variantData.comparePrice > variantData.price) {
        element.textContent = comparePrice;
        element.style.display = "inline";
      } else {
        element.style.display = "none";
      }
    });
  }

  updateAvailability(variantData) {
    this.availabilityElements.forEach((element) => {
      // Find the container to get translation strings
      const container = element.closest("[data-availability-container]");
      const inStockText = container?.dataset.inStockText || "In Stock";
      const outOfStockText =
        container?.dataset.outOfStockText || "Out of Stock";

      // Update text content
      const availabilityText = variantData.available
        ? inStockText
        : outOfStockText;
      element.textContent = availabilityText;
      element.setAttribute(
        "data-availability",
        variantData.available.toString()
      );

      // Update container classes
      if (container) {
        container.classList.remove(
          "product-price__availability--in-stock",
          "product-price__availability--out-of-stock"
        );
        container.classList.add(
          variantData.available
            ? "product-price__availability--in-stock"
            : "product-price__availability--out-of-stock"
        );
      }
    });
  }

  updateSku(variantData) {
    this.skuElements.forEach((element) => {
      element.textContent = variantData.sku || "N/A";
      element.setAttribute("data-sku", variantData.sku || "");
    });
  }

  updateAddToCartButton(variantData) {
    if (!this.addToCartBtn) return;

    const buttonText = this.addToCartBtn.querySelector(
      "[data-add-to-cart-text]"
    );
    const soldOutText = this.addToCartBtn.querySelector("[data-sold-out-text]");

    this.addToCartBtn.disabled = !variantData.available;

    if (variantData.available) {
      if (buttonText) buttonText.style.display = "inline";
      if (soldOutText) soldOutText.style.display = "none";
    } else {
      if (buttonText) buttonText.style.display = "none";
      if (soldOutText) soldOutText.style.display = "inline";
    }
  }

  updateUrl(variantData) {
    const url = new URL(window.location);
    url.searchParams.set("variant", variantData.id);
    window.history.replaceState({}, "", url);
  }

  updateImages(variantData) {
    // Trigger image gallery update if product-image snippet is present
    const imageGallery = this.section.querySelector("[data-product-gallery]");
    if (imageGallery && window.ProductImageGallery) {
      // This would integrate with the product-image snippet
      // For now, we'll just dispatch a custom event
      imageGallery.dispatchEvent(
        new CustomEvent("variant-changed", {
          detail: { variantId: variantData.id },
        })
      );
    }
  }

  updatePickupAvailability(variantData) {
    // Dispatch variant change event for pickup availability component
    this.section.dispatchEvent(
      new CustomEvent("variant:changed", {
        bubbles: true,
        detail: {
          variantId: variantData.id,
          sectionId: this.sectionId,
        },
      })
    );

    // If pickup availability component exists, update it via AJAX
    const pickupContainer = this.section.querySelector(
      "[data-pickup-availability-container]"
    );
    if (pickupContainer && window.PickupAvailability) {
      // The PickupAvailability class will handle the update
      const pickupInstance = pickupContainer.pickupAvailabilityInstance;
      if (pickupInstance) {
        pickupInstance.updatePickupAvailability(variantData.id);
      }
    }
  }

  announceVariantChange(variantData) {
    if (this.priceLiveRegion) {
      const price = this.formatMoney(variantData.price);
      const availability = variantData.available ? "In Stock" : "Out of Stock";
      this.priceLiveRegion.textContent = `Price updated to ${price}, ${availability}`;
    }
  }

  decreaseQuantity() {
    if (!this.quantityInput) return;
    const currentValue = parseInt(this.quantityInput.value) || 1;
    const min = parseInt(this.quantityInput.min) || 1;
    if (currentValue > min) {
      this.quantityInput.value = currentValue - 1;
      this.updateQuantityButtonStates();
    }
  }

  increaseQuantity() {
    if (!this.quantityInput) return;
    const currentValue = parseInt(this.quantityInput.value) || 1;
    const max = parseInt(this.quantityInput.max) || 99;
    if (currentValue < max) {
      this.quantityInput.value = currentValue + 1;
      this.updateQuantityButtonStates();
    }
  }

  validateQuantity() {
    if (!this.quantityInput) return;
    const value = parseInt(this.quantityInput.value);
    const min = parseInt(this.quantityInput.min) || 1;
    const max = parseInt(this.quantityInput.max) || 99;

    if (isNaN(value) || value < min) {
      this.quantityInput.value = min;
    } else if (value > max) {
      this.quantityInput.value = max;
    }

    this.updateQuantityButtonStates();
  }

  updateQuantityButtonStates() {
    if (!this.quantityInput) return;

    const currentValue = parseInt(this.quantityInput.value) || 1;
    const min = parseInt(this.quantityInput.min) || 1;
    const max = parseInt(this.quantityInput.max) || 99;

    // Update minus button state
    if (this.quantityMinus) {
      if (currentValue <= min) {
        this.quantityMinus.disabled = true;
        this.quantityMinus.setAttribute("aria-disabled", "true");
      } else {
        this.quantityMinus.disabled = false;
        this.quantityMinus.setAttribute("aria-disabled", "false");
      }
    }

    // Update plus button state
    if (this.quantityPlus) {
      if (currentValue >= max) {
        this.quantityPlus.disabled = true;
        this.quantityPlus.setAttribute("aria-disabled", "true");
      } else {
        this.quantityPlus.disabled = false;
        this.quantityPlus.setAttribute("aria-disabled", "false");
      }
    }
  }

  async handleAddToCart(e) {
    e.preventDefault(); // Always prevent default form submission
    e.stopPropagation(); // Prevent event bubbling

    if (!this.addToCartBtn || this.addToCartBtn.disabled) {
      return;
    }

    // Prevent multiple simultaneous requests
    if (this.isAddingToCart) {
      console.warn("Add to cart already in progress");
      return;
    }
    this.isAddingToCart = true;

    // Get form data
    const formData = new FormData(this.form);
    const formId = this.form.id;
    
    // Get variant ID - check multiple sources
    let variantId = null;
    
    // 1. Try FormData first
    variantId = formData.get("id");
    
    // 2. Try select dropdown inside the form
    if (!variantId && this.variantSelect) {
      variantId = this.variantSelect.value;
    }
    
    // 3. Try hidden input inside the form
    if (!variantId) {
      const hiddenVariantInput = this.form.querySelector('input[name="id"]');
      if (hiddenVariantInput && !hiddenVariantInput.disabled) {
        variantId = hiddenVariantInput.value;
      }
    }
    
    // 4. Try hidden input associated with form via form attribute (outside the form)
    if (!variantId && formId) {
      const externalInput = document.querySelector(`input[name="id"][form="${formId}"]`);
      if (externalInput && !externalInput.disabled) {
        variantId = externalInput.value;
      }
    }
    
    // 5. Try .product-variant-id class anywhere in the section
    if (!variantId) {
      const variantInput = this.section.querySelector('.product-variant-id:not([disabled])');
      if (variantInput) {
        variantId = variantInput.value;
      }
    }
    
    const quantity = parseInt(formData.get("quantity")) || 1;

    if (!variantId) {
      console.error("No variant ID found. Form:", this.form, "Section:", this.section);
      this.showCartMessage("Please select a variant", "error");
      this.isAddingToCart = false;
      return;
    }

    // Show loading state
    this.addToCartBtn.classList.add("loading");
    this.addToCartBtn.disabled = true;

    const buttonText = this.addToCartBtn.querySelector(
      "[data-add-to-cart-text]"
    );
    const loadingSpinner = this.addToCartBtn.querySelector(
      "[data-add-to-cart-loading]"
    );
    const originalText = buttonText ? buttonText.textContent : "Add to Cart";

    // Hide button text and show loading spinner
    if (buttonText) {
      buttonText.style.display = "none";
    }
    if (loadingSpinner) {
      loadingSpinner.classList.remove("hidden");
      loadingSpinner.style.display = "inline-flex";
    }

    try {
      // Make AJAX request to add item to cart
      const response = await fetch("/cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: JSON.stringify({
          items: [
            {
              id: variantId,
              quantity: quantity,
            },
          ],
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.description || "Failed to add to cart");
      }

      const responseData = await response.json();

      // Fetch updated cart to get the current item count
      const cartResponse = await fetch("/cart.js");
      const cartData = await cartResponse.json();

      // Update cart icon bubble with new count
      this.updateCartIconBubble(cartData.item_count);

      // Reset quantity to 1 after successful add
      if (this.quantityInput) {
        this.quantityInput.value = 1;
        this.updateQuantityButtonStates();
      }

      // Show success message
      this.showCartMessage("Product added to cart!", "success");

      // Trigger cart update event for other components
      document.dispatchEvent(
        new CustomEvent("cart:updated", {
          detail: {
            source: "product-section",
            productVariantId: variantId,
            cartData: cartData,
            itemCount: cartData.item_count,
          },
        })
      );
    } catch (error) {
      console.error("Error adding to cart:", error);
      this.showCartMessage("Error adding to cart. Please try again.", "error");
    } finally {
      // Reset button state
      this.addToCartBtn.classList.remove("loading");
      this.addToCartBtn.disabled = false;
      this.isAddingToCart = false;

      // Show button text and hide loading spinner
      if (buttonText) {
        buttonText.style.display = "inline";
        buttonText.textContent = originalText;
      }
      if (loadingSpinner) {
        loadingSpinner.classList.add("hidden");
        loadingSpinner.style.display = "none";
      }
    }
  }

  updateCartIconBubble(itemCount) {
    // Update all cart icon bubbles on the page
    const cartIcons = document.querySelectorAll('[id^="cart-icon-bubble"]');

    cartIcons.forEach((cartIcon) => {
      let countBadge = cartIcon.querySelector(".menu-single-row__cart-count, .header-icons__cart-count");

      if (itemCount > 0) {
        if (!countBadge) {
          countBadge = document.createElement("sup");
          // Use the correct class based on parent context
          countBadge.className = cartIcon.closest(".header-icons")
            ? "header-icons__cart-count"
            : "menu-single-row__cart-count";
          const icon = cartIcon.querySelector("[data-lucide]");
          if (icon) {
            cartIcon.insertBefore(countBadge, icon);
          } else {
            cartIcon.insertBefore(countBadge, cartIcon.firstChild);
          }
        }
        countBadge.textContent = itemCount;
      } else if (countBadge) {
        countBadge.remove();
      }
    });
  }

  showCartMessage(message, type = "info") {
    // Create or update cart message element
    let messageEl = this.section.querySelector("[data-cart-message]");
    if (!messageEl) {
      messageEl = document.createElement("div");
      messageEl.setAttribute("data-cart-message", "");
      messageEl.setAttribute("aria-live", "polite");
      messageEl.setAttribute("role", "alert");
      messageEl.className =
        "fixed top-4 right-4 p-4 rounded-lg shadow-lg z-50 transition-all duration-300";
      document.body.appendChild(messageEl);
    }

    messageEl.textContent = message;

    // Set appropriate styling based on message type
    let bgColor, textColor;
    switch (type) {
      case "success":
        bgColor = "bg-green-500";
        textColor = "text-white";
        break;
      case "error":
        bgColor = "bg-red-500";
        textColor = "text-white";
        break;
      default:
        bgColor = "bg-blue-500";
        textColor = "text-white";
    }

    messageEl.className = `fixed top-4 right-4 p-4 rounded-lg shadow-lg z-50 transition-all duration-300 ${bgColor} ${textColor}`;

    // Auto-hide after appropriate time based on message type
    const hideDelay = type === "error" ? 5000 : 3000;
    setTimeout(() => {
      if (messageEl.parentNode) {
        messageEl.style.opacity = "0";
        messageEl.style.transform = "translateX(100%)";
        setTimeout(() => {
          if (messageEl.parentNode) {
            messageEl.parentNode.removeChild(messageEl);
          }
        }, 300);
      }
    }, hideDelay);
  }

  handleKeyboard(e) {
    // Handle quantity controls with keyboard
    if (e.target === this.quantityInput) {
      if (e.key === "ArrowUp") {
        e.preventDefault();
        this.increaseQuantity();
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        this.decreaseQuantity();
      }
    }

    // Handle details/summary with keyboard
    if (e.target.tagName === "SUMMARY") {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        e.target.click();
      }
    }
  }

  formatMoney(cents) {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(cents / 100);
  }

  updateVariantInfo() {
    // Initial setup - mark current variant data attributes
    if (this.variantSelect) {
      const selectedOption =
        this.variantSelect.options[this.variantSelect.selectedIndex];
      if (selectedOption) {
        const variantData = {
          id: selectedOption.value,
          price: selectedOption.dataset.price,
          comparePrice: selectedOption.dataset.comparePrice,
          available: selectedOption.dataset.available === "true",
          sku: selectedOption.dataset.sku,
        };

        this.updatePriceDisplay(variantData);
        this.updateAvailability(variantData);
        this.updateSku(variantData);
        this.updateAddToCartButton(variantData);
      }
    }
  }
}

// Area Calculator Class
class AreaCalculator {
  constructor(element) {
    this.calculator = element;
    this.lengthInput = this.calculator.querySelector(
      '[data-area-calculator="length"]'
    );
    this.widthInput = this.calculator.querySelector(
      '[data-area-calculator="width"]'
    );
    this.wasteCheckbox = this.calculator.querySelector(
      '[data-area-calculator="waste"]'
    );
    this.calculateBtn = this.calculator.querySelector(
      '[data-area-calculator="calculate"]'
    );
    this.resetBtn = this.calculator.querySelector(
      '[data-area-calculator="reset"]'
    );
    this.resultsContainer = this.calculator.querySelector(
      '[data-area-calculator="results"]'
    );
    this.areaResult = this.calculator.querySelector(
      '[data-area-calculator="area-result"]'
    );
    this.boxesResult = this.calculator.querySelector(
      '[data-area-calculator="boxes-result"]'
    );

    // Get product data from script element
    const dataElement = this.calculator.querySelector(
      "script[data-area-calculator-data]"
    );
    if (dataElement) {
      try {
        this.productData = JSON.parse(dataElement.textContent);
      } catch (e) {
        console.error("Error parsing area calculator data:", e);
        this.productData = {
          areaPerBox: 1,
          areaUnit: "sq ft",
          measurementUnit: "ft",
        };
      }
    }

    this.init();
  }

  init() {
    this.bindEvents();
  }

  bindEvents() {
    // Calculate button
    if (this.calculateBtn) {
      this.calculateBtn.addEventListener("click", () => this.calculate());
    }

    // Reset button
    if (this.resetBtn) {
      this.resetBtn.addEventListener("click", () => this.reset());
    }

    // Real-time calculation on input change
    if (this.lengthInput) {
      this.lengthInput.addEventListener("input", () =>
        this.handleInputChange()
      );
    }
    if (this.widthInput) {
      this.widthInput.addEventListener("input", () => this.handleInputChange());
    }

    // Recalculate when waste option changes
    if (this.wasteCheckbox) {
      this.wasteCheckbox.addEventListener("change", () =>
        this.handleInputChange()
      );
    }
  }

  handleInputChange() {
    // Auto-calculate if both inputs have values
    const length = parseFloat(this.lengthInput?.value || 0);
    const width = parseFloat(this.widthInput?.value || 0);

    if (length > 0 && width > 0) {
      this.calculate();
    } else {
      this.hideResults();
    }
  }

  calculate() {
    const length = parseFloat(this.lengthInput?.value || 0);
    const width = parseFloat(this.widthInput?.value || 0);
    const includeWaste = this.wasteCheckbox?.checked || false;

    // Validate inputs
    if (length <= 0 || width <= 0) {
      this.showError("Please enter valid dimensions");
      return;
    }

    if (!this.productData || !this.productData.areaPerBox) {
      this.showError("Product area data not available");
      return;
    }

    // Calculate area
    const area = length * width;

    // Add 10% waste if requested
    const totalArea = includeWaste ? area * 1.1 : area;

    // Calculate boxes needed (round up)
    const boxesNeeded = Math.ceil(totalArea / this.productData.areaPerBox);

    // Update display
    this.showResults(area, totalArea, boxesNeeded, includeWaste);
  }

  showResults(originalArea, totalArea, boxesNeeded, includeWaste) {
    if (!this.resultsContainer || !this.areaResult || !this.boxesResult) return;

    // Update area result (show original area, not with waste)
    this.areaResult.textContent = `${originalArea.toFixed(1)} ${
      this.productData.areaUnit
    }`;

    // Update boxes result
    this.boxesResult.textContent = `${boxesNeeded} boxes`;

    // Show results
    this.resultsContainer.classList.remove("hidden");

    // Announce to screen readers
    this.announceResult(originalArea, boxesNeeded);
  }

  showError(message) {
    // Remove any existing error messages
    this.hideResults();

    // Create error message
    let errorElement = this.calculator.querySelector(
      '[data-area-calculator="error"]'
    );
    if (!errorElement) {
      errorElement = document.createElement("div");
      errorElement.setAttribute("data-area-calculator", "error");
      errorElement.className =
        "bg-red-50 border border-red-200 rounded-md p-3 mt-3";
      errorElement.setAttribute("role", "alert");
      this.calculator.appendChild(errorElement);
    }

    errorElement.innerHTML = `
      <div class="flex items-center">
        <svg class="w-4 h-4 text-red-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"></path>
        </svg>
        <span class="text-sm text-red-700">${message}</span>
      </div>
    `;
  }

  hideResults() {
    if (this.resultsContainer) {
      this.resultsContainer.classList.add("hidden");
    }

    // Remove error messages
    const errorElement = this.calculator.querySelector(
      '[data-area-calculator="error"]'
    );
    if (errorElement) {
      errorElement.remove();
    }
  }

  reset() {
    // Clear inputs
    if (this.lengthInput) this.lengthInput.value = "";
    if (this.widthInput) this.widthInput.value = "";

    // Reset waste checkbox
    if (this.wasteCheckbox) this.wasteCheckbox.checked = true;

    // Hide results
    this.hideResults();

    // Focus on first input
    if (this.lengthInput) this.lengthInput.focus();
  }

  announceResult(area, boxes) {
    // Create or update live region for screen readers
    let liveRegion = document.querySelector(
      '[data-area-calculator="live-region"]'
    );
    if (!liveRegion) {
      liveRegion = document.createElement("div");
      liveRegion.setAttribute("data-area-calculator", "live-region");
      liveRegion.setAttribute("aria-live", "polite");
      liveRegion.setAttribute("aria-atomic", "true");
      liveRegion.className = "sr-only";
      document.body.appendChild(liveRegion);
    }

    liveRegion.textContent = `You need approximately ${boxes} boxes for ${area.toFixed(
      1
    )} ${this.productData.areaUnit}`;
  }
}

// Define product-form custom element
if (!customElements.get("product-form")) {
  customElements.define(
    "product-form",
    class ProductForm extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        // Only initialize if not already initialized
        if (!this.productSection) {
          this.productSection = new ProductSection(this);
        }
      }

      disconnectedCallback() {
        // Cleanup if needed
        if (this.productSection) {
          this.productSection = null;
        }
      }
    }
  );
}

// Initialize area calculators and description toggles on DOMContentLoaded
document.addEventListener("DOMContentLoaded", () => {
  // Initialize area calculators
  document.querySelectorAll(".area-calculator").forEach((calculator) => {
    new AreaCalculator(calculator);
  });

  // Description read more/less toggle
  document.querySelectorAll("[data-description-toggle]").forEach((btn) => {
    const wrapper = btn.closest(".product-section__description--truncate");
    if (!wrapper) return;
    btn.addEventListener("click", () => {
      const expanded = wrapper.classList.toggle("is-expanded");
      btn.querySelector("[data-toggle-more]").style.display = expanded ? "none" : "";
      btn.querySelector("[data-toggle-less]").style.display = expanded ? "" : "none";
    });
  });
});
